<template>
  <div class="pm-page so-page">
    <!-- Hero: search, quick actions and how many contracts are complete (US 9371, same layout as US 9370) -->
    <section class="home-hero pm-hero">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ $t('vendor_list.eyebrow', { name: getClient.name }) }}</div>
        <h1 class="home-hero__title">{{ $t('maintenance.third_parties.vendors.title') }}</h1>
        <p class="home-hero__summary">{{ $t('vendor_list.intro') }}</p>

        <div class="home-search pm-search">
          <font-awesome-icon icon="search" class="home-search__icon"/>
          <input
              ref="search"
              v-model="query"
              class="home-search__input"
              :placeholder="$t('vendor_list.search_placeholder')"
              :aria-label="$t('vendor_list.search_placeholder')"
              autocomplete="off"
              qid="vendors-search"
          />
          <span class="home-kbd">{{ isMac ? '⌘K' : 'Ctrl+K' }}</span>
        </div>

        <div class="home-hero__chips pm-hero__chips">
          <router-link v-if="isGranted(permissions.THIRD_PARTY_CREATE)"
              :to="{ name: 'contracts.third_parties.create' }"
              class="home-chip home-chip--light"
              qid="create-vendor-button">
            <font-awesome-icon icon="plus"/>{{ $t('vendor_list.actions.add_third_party') }}
          </router-link>
          <router-link v-if="isGranted(permissions.THIRD_PARTY_CREATE)"
              :to="{ name: 'contracts.create' }"
              class="home-chip"
              qid="create-tp-button">
            <font-awesome-icon icon="file-signature"/>{{ $t('vendor_page.contracts.add') }}
          </router-link>
          <router-link v-if="isGranted(permissions.THIRD_PARTY_CREATE) && getClient.aiModule"
              :to="{ name: 'contracts.agreement_upload' }"
              class="home-chip"
              qid="vendors-upload-agreement">
            <font-awesome-icon icon="sparkles"/>{{ $t('agreement_upload.button') }}
          </router-link>
          <button v-if="overview.modules.projects"
              type="button"
              class="home-chip"
              qid="vendors-get-updated"
              @click="$refs.createProject.showCreateProjectModal()">
            <font-awesome-icon icon="sync-alt"/>{{ $t('vendor_list.actions.get_updated') }}
          </button>
        </div>
      </div>

      <div v-if="openContracts.length" class="home-week pm-progress">
        <div class="home-week__top">
          <svg class="home-ring" width="96" height="96" viewBox="0 0 96 96" aria-hidden="true">
            <circle class="home-ring__track" cx="48" cy="48" r="42"/>
            <circle v-if="completeCount"
                class="home-ring__value"
                cx="48"
                cy="48"
                r="42"
                :stroke-dasharray="ringDash(completeCount, openContracts.length, 42)"/>
          </svg>
          <b class="home-week__count pm-progress__count">{{ completeCount }}/{{ openContracts.length }}</b>
          <div>
            <div class="home-week__title">{{ $t('vendor_list.progress.title') }}</div>
            <div class="home-week__text">{{ $t('vendor_list.progress.text') }}</div>
          </div>
        </div>
        <div class="home-week__stats">
          <div><b>{{ withContracts.length }}</b>{{ $t('vendor_list.progress.third_parties') }}</div>
          <div><b>{{ openContracts.length }}</b>{{ $t('vendor_list.progress.contracts') }}</div>
          <div><b>{{ counts.nobody }}</b>{{ $t('systems_overview.progress.nobody') }}</div>
        </div>
      </div>
    </section>

    <!-- Needs attention -->
    <div v-if="attention.length" class="pm-attention">
      <component :is="item.route ? 'router-link' : 'button'"
          v-for="item in attention"
          :key="item.key"
          :type="item.route ? null : 'button'"
          :to="item.route"
          class="pm-att"
          :qid="'vendors-attention-' + item.key"
          @click="item.tab ? setTab(item.tab) : null">
        <span class="pm-att__dot" :class="'is-' + item.color"></span>
        <span class="pm-att__text">
          <b>{{ item.title }}</b>
          <small>{{ item.text }}</small>
        </span>
        <span class="v2-link">{{ item.action }}</span>
      </component>
    </div>

    <!-- Third parties and their contracts -->
    <section class="v2-card so-card">
      <div class="pm-map__head">
        <div class="pm-map__titles">
          <h3>{{ $t('vendor_list.inventory.title') }}</h3>
          <span class="v2-card__sub">{{ $t('vendor_list.inventory.subtitle') }}</span>
        </div>
        <div v-if="lenses.length > 1" class="v2-seg vl-lens" role="tablist">
          <span v-for="item in lenses"
              :key="item"
              role="tab"
              :class="{ on: activeLens === item }"
              :qid="'vendors-lens-' + item"
              @click="setLens(item)">{{ $t('systems_overview.lens.' + item) }}</span>
        </div>
        <div class="vl-group">
          <span class="v2-card__sub">{{ $t('vendor_list.group.label') }}</span>
          <div class="v2-seg" role="tablist">
            <span v-for="item in ['third_party', 'contract']"
                :key="item"
                role="tab"
                :class="{ on: group === item }"
                :qid="'vendors-group-' + item"
                @click="setGroup(item)">{{ $t('vendor_list.group.' + item) }}</span>
          </div>
        </div>
      </div>

      <div class="so-tabs-row">
        <div class="v2-seg" role="tablist">
          <span v-for="item in tabs"
              :key="item.key"
              role="tab"
              :class="{ on: tab === item.key }"
              :qid="'vendors-tab-' + item.key"
              @click="setTab(item.key)">
            {{ $t('vendor_list.tabs.' + item.key) }}
            <i class="pm-tabs__count" :class="{ 'is-warn': item.warn && item.count }">{{ item.count }}</i>
          </span>
        </div>
      </div>

      <!-- Bulk actions on the ticked contracts, as on the systems list (US 9370) -->
      <div v-if="selected.length" class="so-bulk" qid="contracts-bulk">
        <b>{{ $tc('systems_overview.bulk.selected', selected.length, { count: selected.length }) }}</b>
        <b-dropdown variant="link" toggle-class="so-bulk__btn" no-caret @shown="loadUsers('')" qid="contracts-bulk-owner">
          <template #button-content><font-awesome-icon icon="user"/>{{ $t('systems_overview.bulk.owner') }}</template>
          <b-dropdown-form @submit.prevent>
            <b-form-input v-model="userQuery" size="sm" :placeholder="$t('systems_overview.owner.search')" @input="loadUsers(userQuery)"></b-form-input>
          </b-dropdown-form>
          <b-dropdown-item v-for="user in users" :key="user.id" @click="bulk('owner', { owner: user.id })">{{ user.name }}</b-dropdown-item>
        </b-dropdown>
        <button type="button" class="so-bulk__btn" qid="contracts-bulk-contributors" @click="openBulkModal('contributors')">
          <font-awesome-icon icon="users"/>{{ $t('systems_overview.bulk.contributors') }}
        </button>
        <button type="button" class="so-bulk__btn" qid="contracts-bulk-tags" @click="openBulkModal('tags')">
          <font-awesome-icon icon="tag"/>{{ $t('systems_overview.bulk.tags') }}
        </button>
        <button type="button" class="so-bulk__close" :aria-label="$t('systems_overview.bulk.clear')" @click="selected = []">
          <font-awesome-icon icon="times"/>
        </button>
      </div>

      <div ref="table" class="so-table">
        <div class="so-row so-row--head" :style="gridStyle">
          <span><input v-if="selectableContracts.length" type="checkbox" class="so-check" :checked="allSelected" :aria-label="$t('systems_overview.bulk.select_all')" @change="toggleAll($event.target.checked)"/></span>
          <span>{{ group === 'contract' ? $t('vendor_list.columns.contract') : $t('vendor_list.columns.third_party') }}</span>
          <span v-for="column in columns" :key="column">{{ $t('vendor_list.columns.' + column) }}</span>
          <span></span>
        </div>

        <div v-if="loading" class="home-empty">{{ $t('vendor_list.loading') }}</div>
        <template v-else>
          <template v-for="row in rows">
            <!-- A third party -->
            <div v-if="row.type === 'vendor'"
                :key="'v' + row.vendor.id"
                class="so-row vl-vendor"
                :class="{ 'is-dim': !row.vendor.contracts.length }"
                :style="gridStyle">
              <span>
                <button v-if="row.contracts.length"
                    type="button"
                    class="vl-toggle"
                    :aria-expanded="isOpen(row.vendor) ? 'true' : 'false'"
                    :aria-label="row.vendor.name"
                    @click="toggleOpen(row.vendor)">
                  <font-awesome-icon :icon="isOpen(row.vendor) ? 'chevron-down' : 'chevron-right'"/>
                </button>
              </span>
              <span class="so-name">
                <span v-if="row.vendor.logo" class="so-logo has-image"><img :src="row.vendor.logo" alt=""></span>
                <span v-else class="so-logo" :style="{ background: logoColor(row.vendor.name) }">{{ initials(row.vendor.name) }}</span>
                <span class="so-name__text">
                  <router-link :to="{ name: 'contracts.third_parties.view', params: { slug: row.vendor.slug } }" class="so-name__link" qid="vendor-view-page-link">{{ row.vendor.name }}</router-link>
                  <small v-if="row.vendor.from_workspace" class="so-from"><font-awesome-icon icon="lock"/>{{ $t('systems_overview.from_workspace', { name: row.vendor.from_workspace }) }}</small>
                  <small v-else>{{ vendorSubline(row.vendor) }}</small>
                </span>
              </span>
              <span v-for="column in columns" :key="column" class="so-cell">
                <template v-if="column === 'owner'">
                  <span v-if="vendorOwners(row.vendor).length" class="so-muted">{{ vendorOwners(row.vendor).join(', ') | truncate(40) }}</span>
                </template>
                <template v-else-if="column === 'data'">
                  <span class="so-two">{{ countryLine(vendorCountries(row.vendor)) }}<small v-if="outside(vendorCountries(row.vendor)).length" class="is-warn">{{ $t('vendor_list.outside', { list: outside(vendorCountries(row.vendor)).join(', ') }) }}</small></span>
                </template>
                <template v-else-if="column === 'used'">
                  <span class="so-chips">
                    <span v-if="row.vendor.sub_processor_on && !row.vendor.contracts.length" class="so-use">{{ $tc('vendor_list.sub_processor_on', row.vendor.sub_processor_on, { count: row.vendor.sub_processor_on }) }}</span>
                    <span v-else-if="!row.vendor.contracts.length" class="so-use so-use--none">{{ $t('vendor_list.no_contract') }}</span>
                    <span v-else class="so-use">{{ $tc('vendor_hero.contracts', row.vendor.contracts.length, { count: row.vendor.contracts.length }) }}</span>
                  </span>
                </template>
                <template v-else-if="column === 'criticality' && row.vendor.contracts.length">
                  <span class="so-cia">
                    <b v-for="key in CIA" :key="key" :class="ciaClass(vendorCriticality(row.vendor)[key])" :title="ciaTitle(key, vendorCriticality(row.vendor)[key])">{{ impactLevel(vendorCriticality(row.vendor)[key]) || '–' }}</b>
                  </span>
                </template>
              </span>
              <span class="so-kebab"></span>
            </div>

            <!-- A contract -->
            <div v-else
                :key="'c' + row.contract.id + '-' + (row.vendor ? row.vendor.id : 0)"
                class="so-row vl-contract"
                :class="{ 'is-nested': group === 'third_party', 'is-selected': selected.includes(row.contract.id) }"
                :style="gridStyle">
              <span>
                <input v-if="canEdit(row.contract)"
                    type="checkbox"
                    class="so-check"
                    :checked="selected.includes(row.contract.id)"
                    :aria-label="row.contract.name"
                    @change="toggle(row.contract.id, $event.target.checked)"/>
              </span>
              <span class="so-name">
                <span class="vl-contract__icon"><font-awesome-icon icon="file-alt"/></span>
                <span class="so-name__text">
                  <router-link :to="{ name: 'contracts.view', params: { slug: row.contract.slug } }" class="so-name__link" qid="tp-view-page-link">{{ row.contract.name }}</router-link>
                  <small>{{ contractSubline(row) }}</small>
                </span>
              </span>
              <span v-for="column in columns" :key="column" class="so-cell">
                <!-- Owner, editable in place -->
                <template v-if="column === 'owner'">
                  <b-dropdown v-if="canEdit(row.contract)"
                      variant="link"
                      toggle-class="so-inline"
                      no-caret
                      :qid="'contract-owner-' + row.contract.slug"
                      @shown="loadUsers('')">
                    <template #button-content>
                      <span v-if="row.contract.owner" class="so-owner"><span class="so-avatar">{{ initials(row.contract.owner.name) }}</span>{{ row.contract.owner.name }}</span>
                      <span v-else class="so-chip so-chip--miss"><font-awesome-icon icon="plus"/>{{ $t('systems_overview.owner.assign') }}</span>
                    </template>
                    <b-dropdown-form @submit.prevent>
                      <b-form-input v-model="userQuery" size="sm" :placeholder="$t('systems_overview.owner.search')" @input="loadUsers(userQuery)"></b-form-input>
                    </b-dropdown-form>
                    <b-dropdown-item v-for="user in users" :key="user.id" :active="row.contract.owner && row.contract.owner.id === user.id" @click="setOwner(row.contract, user)">{{ user.name }}</b-dropdown-item>
                    <b-dropdown-text class="so-hint">{{ $t('vendor_list.owner_hint') }}</b-dropdown-text>
                  </b-dropdown>
                  <span v-else-if="row.contract.owner" class="so-owner"><span class="so-avatar">{{ initials(row.contract.owner.name) }}</span>{{ row.contract.owner.name }}</span>
                  <span v-else class="so-chip so-chip--miss"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('systems_overview.owner.none') }}</span>
                </template>
                <template v-else-if="column === 'data'">
                  <span class="so-two">{{ countryLine(row.contract.countries) }}<small v-if="outside(row.contract.countries).length" :class="{ 'is-warn': row.contract.missing.includes('transfer_basis') }">{{ basisLine(row.contract) }}</small></span>
                </template>
                <template v-else-if="column === 'used'">
                  <span class="so-chips">
                    <span v-if="overview.modules.systems && row.contract.systems" class="so-use so-use--gdpr"><font-awesome-icon icon="desktop"/>{{ $tc('contract_page.short.systems', row.contract.systems, { count: row.contract.systems }) }}</span>
                    <span v-if="overview.modules.activities && row.contract.activities" class="so-use so-use--gdpr"><font-awesome-icon icon="project-diagram"/>{{ $tc('contract_page.short.activities', row.contract.activities, { count: row.contract.activities }) }}</span>
                    <span v-if="overview.modules.risk && row.contract.risks" class="so-use so-use--grc"><font-awesome-icon icon="shield-alt"/>{{ $tc('systems_overview.used.risks', row.contract.risks, { count: row.contract.risks }) }}</span>
                    <span v-if="!row.contract.systems && !row.contract.activities && !row.contract.risks" class="so-use so-use--none">{{ $t('systems_overview.used.none') }}</span>
                  </span>
                </template>
                <template v-else-if="column === 'dpa'">
                  <span v-if="row.contract.dpa === 'Yes'" class="pm-status is-ok"><font-awesome-icon icon="check"/>{{ $t('system_page.where.dpa') }}</span>
                  <span v-else-if="row.contract.missing.includes('dpa')" class="pm-status is-warn"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('system_page.where.no_dpa') }}</span>
                  <span v-else class="so-muted">{{ row.contract.dpa ? value('data_processor_agreement', row.contract.dpa) : '–' }}</span>
                </template>
                <template v-else-if="column === 'criticality'">
                  <span class="so-cia">
                    <b v-for="key in CIA" :key="key" :class="ciaClass(row.contract.criticality[key])" :title="ciaTitle(key, row.contract.criticality[key])">{{ impactLevel(row.contract.criticality[key]) || '–' }}</b>
                  </span>
                </template>
                <template v-else-if="column === 'dependency'">
                  <span v-if="row.contract.critical_systems" class="so-two" :class="{ 'is-warn': share(row.contract) >= 50 }">
                    {{ $tc('vendor_list.risk.critical_systems', row.contract.critical_systems, { count: row.contract.critical_systems }) }}
                    <small v-if="overview.critical_systems">{{ $t('vendor_list.risk.share', { share: share(row.contract) }) }}</small>
                  </span>
                  <span v-else class="so-muted">{{ row.contract.systems ? $t('vendor_list.risk.no_critical') : '–' }}</span>
                </template>
                <template v-else-if="column === 'risks'">
                  <span v-if="row.contract.risks" class="so-use so-use--grc">{{ $tc('systems_overview.used.risks', row.contract.risks, { count: row.contract.risks }) }}</span>
                  <span v-else-if="row.contract.critical_systems" class="pm-status is-warn"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('vendor_list.risk.not_assessed') }}</span>
                  <span v-else class="so-muted">–</span>
                </template>
                <template v-else-if="column === 'measures'">
                  <span v-if="row.contract.measures" class="so-muted">{{ $tc('vendor_list.risk.measures', row.contract.measures, { count: row.contract.measures }) }}</span>
                  <span v-else-if="row.contract.critical_systems" class="pm-status is-warn"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('vendor_list.risk.no_measures') }}</span>
                  <span v-else class="so-muted">–</span>
                </template>
                <template v-else-if="column === 'confirmed'">
                  <span class="so-two" :class="confirmedClass(row.contract)">
                    {{ confirmedText(row.contract) }}
                    <small>{{ confirmedSub(row.contract) }}</small>
                  </span>
                </template>
                <template v-else-if="column === 'status'">
                  <span v-if="row.contract.ended_at" class="pm-status is-ended"><font-awesome-icon icon="archive"/>{{ $t('contract_end.ended_on', { date: endedOn(row.contract.ended_at) }) }}</span>
                  <span v-else-if="!row.contract.missing.length" class="pm-status is-ok"><font-awesome-icon icon="check"/>{{ $t('systems_overview.status.complete') }}</span>
                  <span v-else class="pm-status is-warn" :title="missingTitle(row.contract)"><font-awesome-icon icon="exclamation-triangle"/>{{ $tc('systems_overview.status.missing', row.contract.missing.length, { count: row.contract.missing.length }) }}</span>
                </template>
              </span>
              <span class="so-kebab">
                <b-dropdown variant="link" toggle-class="so-kebab__btn" no-caret right :qid="'contract-row-menu-' + row.contract.slug">
                  <template #button-content><font-awesome-icon icon="ellipsis-h"/></template>
                  <b-dropdown-item :to="{ name: 'contracts.view', params: { slug: row.contract.slug } }">{{ $t('systems_overview.row.open') }}</b-dropdown-item>
                  <b-dropdown-item v-if="row.vendor" :to="{ name: 'contracts.third_parties.view', params: { slug: row.vendor.slug } }">{{ $t('contract_hero.menu.open_vendor', { name: row.vendor.name }) }}</b-dropdown-item>
                </b-dropdown>
              </span>
            </div>
          </template>
          <div v-if="!rows.length" class="home-empty">{{ query ? $t('systems_overview.empty_search') : $t('vendor_list.empty_tab') }}</div>
        </template>
      </div>

      <!-- SaaS and hosted systems without a contract (US 9370 Runs as) -->
      <div v-if="!loading && systemsWithoutContract.total && tab === 'in_use'" class="vl-found" qid="vendors-systems-without-contract">
        <div class="vl-found__head">
          <font-awesome-icon icon="sparkles"/>
          <div>
            <b>{{ $tc('vendor_list.found.title', systemsWithoutContract.total, { count: systemsWithoutContract.total }) }}</b>
            <small>{{ $t('vendor_list.found.text') }}</small>
          </div>
        </div>
        <div v-for="system in systemsWithoutContract.items" :key="system.id" class="vl-found__row">
          <span class="so-logo" :style="{ background: logoColor(system.name) }">{{ initials(system.name) }}</span>
          <router-link :to="{ name: 'systems.view', params: { slug: system.slug } }" class="so-name__link">{{ system.name }}</router-link>
          <span class="so-muted">{{ $t('systems_overview.runs_as.' + system.runs_as) }}</span>
          <span class="pm-grow"></span>
          <router-link :to="{ name: 'systems.view', params: { slug: system.slug } }" class="v2-link">{{ $t('vendor_list.found.link') }}</router-link>
        </div>
      </div>

      <div v-if="!loading" class="pm-foot so-foot">
        <span>{{ $t('vendor_list.legend') }}</span>
        <span class="so-foot__count">{{ $t('vendor_list.showing', { shown: shownCount, total: tabCount }) }}</span>
      </div>
    </section>

    <!-- Contributors or tags for the ticked contracts -->
    <b-modal ref="bulk-modal" hide-footer @hidden="bulkValues = []">
      <div class="d-block text-center">
        <h4>{{ bulkMode ? $t('vendor_list.bulk.' + bulkMode + '_title') : '' }}</h4>
      </div>
      <b-form-group>
        <multiselect v-if="bulkMode === 'contributors'"
            v-model="bulkValues"
            :options="users"
            :multiple="true"
            track-by="id"
            label="name"
            :select-label="$t('system.dropdown_select_label')"
            :selected-label="$t('system.dropdown_selected_label')"
            :deselect-label="$t('system.dropdown_deselect_label')"
            :placeholder="$t('systems_overview.owner.search')"
            qid="contracts-bulk-contributors-select"
            @search-change="loadUsers"
        ></multiselect>
        <multiselect v-else
            v-model="bulkValues"
            :options="tags"
            :multiple="true"
            :taggable="true"
            track-by="name_translated"
            label="name_translated"
            :select-label="$t('system.dropdown_select_label')"
            :selected-label="$t('system.dropdown_selected_label')"
            :deselect-label="$t('system.dropdown_deselect_label')"
            :placeholder="$t('systems.create.tags_placeholder')"
            qid="contracts-bulk-tags-select"
            @search-change="loadTags"
            @tag="addTag"
        ></multiselect>
      </b-form-group>
      <div class="text-right">
        <b-button class="mt-3" variant="secondary" @click="$refs['bulk-modal'].hide()">{{ $t('systems.delete.cancel') }}</b-button>
        <b-button class="mt-3 ml-3"
            variant="success"
            :disabled="!bulkValues.length"
            qid="contracts-bulk-submit"
            @click="submitBulkModal">
          {{ $t('systems_overview.bulk.apply') }}
        </b-button>
      </div>
    </b-modal>

    <span class="pm-hidden-trigger">
      <CreateProjectModal v-if="getClient.projectModule"
          ref="createProject"
          type="update"
          :types="['update', 'validation']"
          scope="contracts"></CreateProjectModal>
    </span>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {isValidBasis, basisLabel} from '@/util/transferBasis.js';
  import {endedOn} from '@/util/contractEnd.js';
  import {impactClass, impactLevel, impactKey, IMPACT_LEVELS} from '@/util/impactLevels.js';
  import moment from 'moment';
  import CreateProjectModal from '@/views/Projects/CreateProjectModal.vue';

  const WIDTHS = {
    owner: '140px', data: '150px', used: '160px', dpa: '96px', status: '110px',
    criticality: '96px', dependency: '150px', risks: '120px', measures: '110px', confirmed: '118px'
  };
  const CIA = ['confidentiality', 'integrity', 'availability'];
  // Columns that give way first when the table is too narrow; owner and status always stay
  const DROP_ORDER = ['measures', 'confirmed', 'data', 'dpa', 'risks', 'dependency', 'criticality', 'used'];
  const NAME_WIDTH = 160;
  // Tick box, name, menu and the row's padding
  const FIXED_WIDTH = 22 + NAME_WIDTH + 28 + 40;
  const GAP = 14;
  const LOGO_COLORS = ['#1F7A72', '#3B6FD8', '#7B5CD6', '#D9532B', '#0E7C86', '#C27C00', '#5B6573', '#C2417A'];
  const STORAGE_KEY = 'third-parties-list';

  export default {
    name: 'ThirdPartiesList',
    components: {
      CreateProjectModal
    },
    data() {
      return {
        overview: { modules: {}, third_parties: [], unlinked_contracts: [], systems_without_contract: [] },
        // Contracts still with more than one third party, from before each had one (US 9369 + 9371)
        sharedContracts: 0,
        loading: true,
        query: '',
        tab: 'in_use',
        group: 'third_party',
        lens: 'privacy',
        tableWidth: 0,
        CIA: CIA,
        closed: [],
        selected: [],
        users: [],
        userQuery: '',
        tags: [],
        bulkMode: '',
        bulkValues: [],
        isMac: typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      withContracts() {
        return this.overview.third_parties.filter(vendor => vendor.contracts.length);
      },
      // Every contract once, also those without a third party
      contracts() {
        let seen = {};
        let list = [];
        this.overview.third_parties.forEach(vendor => vendor.contracts.forEach(contract => {
          if (!seen[contract.id]) {
            seen[contract.id] = true;
            list.push(contract);
          }
        }));
        return list.concat(this.overview.unlinked_contracts.filter(contract => !seen[contract.id]));
      },
      // Contracts that haven't ended; an ended one is only listed on its own tab
      openContracts() {
        return this.contracts.filter(contract => !contract.ended_at);
      },
      completeCount() {
        return this.openContracts.filter(contract => !contract.missing.length).length;
      },
      counts() {
        return {
          nobody: this.openContracts.filter(contract => !contract.owner).length,
          missing: this.openContracts.filter(contract => contract.missing.length).length,
          ended: this.contracts.length - this.openContracts.length,
          subOnly: this.overview.third_parties.filter(vendor => !vendor.contracts.length && vendor.sub_processor_on).length,
          notLinked: this.overview.third_parties.filter(vendor => !vendor.contracts.length && !vendor.sub_processor_on).length
        };
      },
      tabs() {
        let tabs = [
          { key: 'in_use', count: this.group === 'contract' ? this.openContracts.length : this.withContracts.filter(vendor => vendor.contracts.some(contract => !contract.ended_at)).length },
          { key: 'nobody', count: this.counts.nobody, warn: true },
          { key: 'missing', count: this.counts.missing, warn: true }
        ];
        if (this.overview.modules.sub_processors) {
          tabs.push({ key: 'sub_only', count: this.counts.subOnly });
        }
        tabs.push({ key: 'not_linked', count: this.counts.notLinked });
        if (this.counts.ended) {
          tabs.push({ key: 'ended', count: this.counts.ended });
        }
        return tabs;
      },
      // Privacy needs GDPR, Risk needs the risk module; Security is always there, as on the systems list (US 9370)
      lenses() {
        let modules = this.overview.modules;
        return ['privacy', 'security', 'risk'].filter(lens => (lens !== 'privacy' || modules.gdpr) && (lens !== 'risk' || modules.risk));
      },
      activeLens() {
        return this.lenses.includes(this.lens) ? this.lens : this.lenses[0];
      },
      lensColumns() {
        let modules = this.overview.modules;
        let columns = {
          privacy: ['owner', 'data', 'used', 'dpa', 'status'],
          security: ['owner', 'criticality', 'dependency', 'measures', 'confirmed', 'status'],
          risk: ['owner', 'criticality', 'dependency', 'risks', 'measures', 'status']
        }[this.activeLens];
        return columns.filter(column => ({
          data: modules.gdpr,
          dpa: modules.gdpr,
          criticality: modules.systems,
          dependency: modules.systems,
          confirmed: modules.projects
        })[column] !== false);
      },
      // The lens's columns that fit the table's width
      columns() {
        let columns = this.lensColumns.slice();
        let needed = list => FIXED_WIDTH + list.reduce((sum, column) => sum + parseInt(WIDTHS[column], 10), 0) + (list.length + 2) * GAP;
        let drop = DROP_ORDER.filter(column => columns.includes(column));
        while (this.tableWidth && needed(columns) > this.tableWidth && drop.length) {
          let column = drop.shift();
          columns = columns.filter(item => item !== column);
        }
        return columns;
      },
      gridStyle() {
        return { gridTemplateColumns: '22px minmax(' + NAME_WIDTH + 'px, 1fr) ' + this.columns.map(column => WIDTHS[column]).join(' ') + ' 28px' };
      },
      contractFilter() {
        return {
          in_use: contract => !contract.ended_at,
          nobody: contract => !contract.ended_at && !contract.owner,
          missing: contract => !contract.ended_at && contract.missing.length > 0,
          ended: contract => !!contract.ended_at
        }[this.tab] || null;
      },
      // The rows to show: third parties with their (open) contracts, or a flat list of contracts
      rows() {
        let query = this.query.trim().toLowerCase();
        let matchContract = contract => !query || [contract.name, contract.owner ? contract.owner.name : '']
          .concat(contract.countries.map(country => country.name)).some(value => value && value.toLowerCase().includes(query));
        let matchVendor = vendor => !query || [vendor.name, vendor.country].some(value => value && value.toLowerCase().includes(query));
        let rows = [];

        if (['sub_only', 'not_linked'].includes(this.tab)) {
          this.overview.third_parties
            .filter(vendor => !vendor.contracts.length && (this.tab === 'sub_only' ? vendor.sub_processor_on : !vendor.sub_processor_on))
            .filter(matchVendor)
            .forEach(vendor => rows.push({ type: 'vendor', vendor: vendor, contracts: [] }));
          return rows;
        }

        let keep = this.contractFilter;
        if (this.group === 'contract') {
          let vendorOf = {};
          this.overview.third_parties.forEach(vendor => vendor.contracts.forEach(contract => {
            vendorOf[contract.id] = vendorOf[contract.id] || vendor;
          }));
          return this.contracts.filter(keep).filter(contract => matchContract(contract) || (vendorOf[contract.id] && matchVendor(vendorOf[contract.id])))
            .map(contract => ({ type: 'contract', contract: contract, vendor: vendorOf[contract.id] || null }));
        }

        this.withContracts.forEach(vendor => {
          let contracts = vendor.contracts.filter(keep);
          if (!contracts.length) {
            return;
          }
          if (query && !matchVendor(vendor)) {
            contracts = contracts.filter(matchContract);
            if (!contracts.length) {
              return;
            }
          }
          rows.push({ type: 'vendor', vendor: vendor, contracts: contracts });
          if (this.isOpen(vendor) || query) {
            contracts.forEach(contract => rows.push({ type: 'contract', contract: contract, vendor: vendor }));
          }
        });
        // Contracts nobody linked to a third party yet, at the end
        let loose = this.overview.unlinked_contracts.filter(keep).filter(matchContract);
        if (loose.length) {
          rows.push({ type: 'vendor', vendor: { id: 0, slug: '', name: this.$t('vendor_list.no_third_party'), contracts: loose, sub_processor_on: 0 }, contracts: loose });
          loose.forEach(contract => rows.push({ type: 'contract', contract: contract, vendor: null }));
        }
        return rows;
      },
      shownCount() {
        return this.rows.filter(row => this.group === 'contract' ? row.type === 'contract' : row.type === 'vendor').length;
      },
      tabCount() {
        let tab = this.tabs.find(item => item.key === this.tab);
        return tab ? tab.count : 0;
      },
      systemsWithoutContract() {
        let value = this.overview.systems_without_contract;
        return value && value.items ? value : { total: 0, items: [] };
      },
      // Contracts shown now that the user may change (not those of the parent workspace)
      selectableContracts() {
        return this.rows.filter(row => row.type === 'contract' && this.canEdit(row.contract)).map(row => row.contract);
      },
      allSelected() {
        return this.selectableContracts.length > 0 && this.selectableContracts.every(contract => this.selected.includes(contract.id));
      },
      attention() {
        let items = [];
        let counts = this.counts;
        let gdpr = this.overview.modules.gdpr;
        if (counts.nobody) {
          items.push({ key: 'nobody', color: 'amber', tab: 'nobody',
            title: this.$tc('vendor_list.attention.nobody_title', counts.nobody, { count: counts.nobody }),
            text: this.$t('vendor_list.attention.nobody_text'), action: this.$t('systems_overview.attention.assign') });
        }
        let noDpa = this.openContracts.filter(contract => contract.missing.includes('dpa')).length;
        if (gdpr && noDpa) {
          items.push({ key: 'dpa', color: 'red', tab: 'missing',
            title: this.$tc('vendor_list.attention.dpa_title', noDpa, { count: noDpa }),
            text: this.$t('vendor_list.attention.dpa_text'), action: this.$t('systems_overview.attention.review') });
        }
        let noBasis = this.openContracts.filter(contract => contract.missing.includes('transfer_basis')).length;
        if (gdpr && noBasis) {
          items.push({ key: 'basis', color: 'blue', tab: 'missing',
            title: this.$tc('vendor_list.attention.basis_title', noBasis, { count: noBasis }),
            text: this.$t('vendor_list.attention.basis_text'), action: this.$t('systems_overview.attention.review') });
        }
        let systems = this.systemsWithoutContract;
        if (systems.total) {
          items.push({ key: 'systems', color: 'violet', route: { name: 'systems.view', params: { slug: systems.items[0].slug } },
            title: this.$tc('vendor_list.found.title', systems.total, { count: systems.total }),
            text: systems.items.slice(0, 3).map(system => system.name).join(', '), action: this.$t('systems_overview.attention.open') });
        }
        if (this.sharedContracts) {
          items.unshift({ key: 'shared', color: 'amber', route: { name: 'shared_links', query: { tab: 'contracts' } },
            title: this.$tc('shared_links.attention_contracts', this.sharedContracts, { count: this.sharedContracts }),
            text: this.$t('shared_links.attention_contracts_text'), action: this.$t('shared_links.sort_out') });
        }
        return items.slice(0, 4);
      }
    },
    watch: {
      getClient() {
        this.selected = [];
        this.loadOverview();
      },
      tab() {
        this.selected = [];
      }
    },
    methods: {
      endedOn: endedOn,
      ciaClass: impactClass,
      impactLevel: impactLevel,
      ciaTitle(key, value) {
        return this.$t('systems_overview.criticality.' + key) + (value ? ': ' + this.$t('impact.' + impactKey(value)) : '');
      },
      // The highest impact per C/I/A over a third party's contracts
      vendorCriticality(vendor) {
        let highest = {};
        CIA.forEach(key => {
          highest[key] = vendor.contracts.map(contract => contract.criticality[key])
            .filter(value => value)
            .sort((a, b) => IMPACT_LEVELS.indexOf(a) - IMPACT_LEVELS.indexOf(b))
            .pop() || null;
        });
        return highest;
      },
      // How much of the workspace's critical systems depend on the contract, in percent
      share(contract) {
        let total = this.overview.critical_systems || 0;
        return total ? Math.min(100, Math.round(100 * contract.critical_systems / total)) : 0;
      },
      confirmedClass(contract) {
        if (contract.open_task) {
          return contract.open_task.overdue ? 'is-late' : 'is-open';
        }
        return contract.last_confirmed ? 'is-ok' : 'is-none';
      },
      confirmedText(contract) {
        if (contract.open_task) {
          return contract.open_task.overdue ? this.$t('systems_overview.confirmed.overdue') : this.$t('systems_overview.confirmed.open');
        }
        return contract.last_confirmed ? moment(contract.last_confirmed).format('D MMM YYYY') : this.$t('systems_overview.confirmed.never');
      },
      confirmedSub(contract) {
        if (contract.open_task) {
          return contract.open_task.project;
        }
        return contract.last_confirmed ? '' : this.$t('systems_overview.confirmed.not_in_project');
      },
      setLens(value) {
        this.lens = value;
        this.remember();
      },
      loadOverview() {
        let self = this;
        if (!this.getClient || !this.getClient.slug) {
          return;
        }
        piincHttp.get('vendors-overview', { params: { client: this.getClient.slug } }).then(function(response) {
          self.overview = response.data;
          self.loading = false;
        }, function() {
          self.loading = false;
        });
        piincHttp.get('shared-links', { params: { client: this.getClient.slug } }).then(function(response) {
          self.sharedContracts = response.data.contracts.length;
        }, function() {
          self.sharedContracts = 0;
        });
      },
      canEdit(contract) {
        return !contract.from_workspace && this.isGranted([this.permissions.THIRD_PARTY_EDIT, this.permissions.THIRD_PARTY_EDIT_OWN], contract);
      },
      toggle(id, checked) {
        this.selected = checked ? _.uniq(this.selected.concat([id])) : this.selected.filter(item => item !== id);
      },
      toggleAll(checked) {
        let ids = this.selectableContracts.map(contract => contract.id);
        this.selected = checked ? _.uniq(this.selected.concat(ids)) : this.selected.filter(id => !ids.includes(id));
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params: { client: this.getClient.id, keyword: query || undefined, per_page: 50 } }).then(function(response) {
          self.users = response.data.items;
        }, function() {});
      },
      loadTags(query) {
        let self = this;
        piincHttp.get('tags', { params: { client: this.getClient.slug, keyword: query || undefined } }).then(function(response) {
          self.tags = response.data;
        }, function() {});
      },
      addTag(name) {
        let tag = { name: name, name_translated: name };
        this.tags.push(tag);
        this.bulkValues.push(tag);
      },
      // The owner becomes accountable; everyone else on the contract stays as a contributor
      setOwner(contract, user) {
        let self = this;
        let users = _.uniq(contract.users.map(member => member.id).concat([user.id]));
        piincHttp.put('third-parties/' + contract.slug, { users: users, owner: user.id }).then(function() {
          self.$toastr('success', self.$t('systems_overview.saved'));
          self.loadOverview();
        }, function() {});
      },
      openBulkModal(mode) {
        this.bulkMode = mode;
        this.bulkValues = [];
        if (mode === 'contributors') {
          this.loadUsers('');
        } else {
          this.loadTags('');
        }
        this.$refs['bulk-modal'].show();
      },
      submitBulkModal() {
        let values = this.bulkMode === 'contributors'
          ? { users: this.bulkValues.map(user => user.id) }
          : { tags: this.bulkValues.map(tag => tag.name_translated) };
        this.bulk(this.bulkMode, values);
        this.$refs['bulk-modal'].hide();
      },
      bulk(action, values) {
        let self = this;
        piincHttp.put('third-parties/bulk', Object.assign({ ids: this.selected, action: action }, values || {})).then(function(response) {
          let skipped = response.data.skipped.length;
          self.$toastr(skipped ? 'warning' : 'success', skipped
            ? self.$tc('vendor_list.bulk.skipped', skipped, { count: skipped })
            : self.$tc('vendor_list.bulk.done', response.data.updated.length, { count: response.data.updated.length }));
          self.selected = [];
          self.loadOverview();
        }, function() {});
      },
      isOpen(vendor) {
        return !this.closed.includes(vendor.id);
      },
      toggleOpen(vendor) {
        this.closed = this.isOpen(vendor) ? this.closed.concat([vendor.id]) : this.closed.filter(id => id !== vendor.id);
      },
      setTab(key) {
        this.tab = key;
      },
      setGroup(value) {
        this.group = value;
        this.remember();
      },
      remember() {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ group: this.group, lens: this.lens }));
        } catch (e) {
          // Ignore blocked storage
        }
      },
      initials(name) {
        let words = (name || '').trim().split(/\s+/).filter(Boolean);
        return words.length > 1 ? (words[0][0] + words[1][0]).toUpperCase() : (words[0] || '?').slice(0, 2).toUpperCase();
      },
      logoColor(name) {
        let sum = 0;
        for (let i = 0; i < (name || '').length; i++) {
          sum += name.charCodeAt(i);
        }
        return LOGO_COLORS[sum % LOGO_COLORS.length];
      },
      role(value) {
        let key = 'maintenance.third_parties.processing_role.' + this.$options.filters.slugify(value);
        return this.$te(key) ? this.$t(key) : value;
      },
      value(group, value) {
        let key = 'maintenance.third_parties.' + group + '.' + this.$options.filters.slugify(value);
        return this.$te(key) ? this.$t(key) : value;
      },
      vendorSubline(vendor) {
        let parts = [];
        if (vendor.country) {
          parts.push(vendor.country);
        }
        if (vendor.contracts.length) {
          parts.push(this.$tc('vendor_hero.contracts', vendor.contracts.length, { count: vendor.contracts.length }));
        }
        if (vendor.sub_processor_on) {
          parts.push(this.$tc('vendor_list.sub_processor_on', vendor.sub_processor_on, { count: vendor.sub_processor_on }));
        }
        return parts.join(' · ');
      },
      // Second line under a contract: its role and what it covers, or its third party in the flat list
      contractSubline(row) {
        let contract = row.contract;
        let parts = [];
        if (this.group === 'contract' && row.vendor) {
          parts.push(row.vendor.name);
        }
        if (contract.roles.length) {
          parts.push(contract.roles.map(this.role).join(', '));
        }
        if (contract.covers) {
          parts.push(this.$t('systems_overview.covers.' + contract.covers));
        }
        return parts.join(' · ');
      },
      vendorOwners(vendor) {
        let names = [];
        vendor.contracts.forEach(contract => {
          if (contract.owner && !names.includes(contract.owner.name)) {
            names.push(contract.owner.name);
          }
        });
        return names;
      },
      vendorCountries(vendor) {
        let seen = {};
        let list = [];
        vendor.contracts.forEach(contract => contract.countries.forEach(country => {
          if (!seen[country.name]) {
            seen[country.name] = true;
            list.push(country);
          }
        }));
        return list;
      },
      outside(countries) {
        return countries.filter(country => country.eea === false).map(country => country.name);
      },
      // EU/EEA countries first, or "Not filled in yet"
      countryLine(countries) {
        if (!countries.length) {
          return this.$t('contract_page.data.no_locations');
        }
        let inside = countries.filter(country => country.eea !== false).map(country => country.name);
        return inside.length ? inside.join(', ') : this.$t('vendor_list.only_outside');
      },
      // Countries outside the EU/EEA with their bases, or which of them have none (US 9371)
      basisLine(contract) {
        let outside = contract.countries.filter(country => country.eea === false);
        let missing = outside.filter(country => !isValidBasis(country.basis));
        if (missing.length) {
          return this.$t('vendor_list.outside_no_basis', { list: missing.map(country => country.name).join(', ') });
        }
        let bases = _.uniq(outside.map(country => basisLabel(this, country.basis)));
        return this.$t('vendor_list.outside_basis', { list: outside.map(country => country.name).join(', '), basis: bases.join(', ') });
      },
      missingTitle(contract) {
        return contract.missing.map(key => this.$t('vendor_list.missing.' + key)).join(', ');
      },
      ringDash(value, total, radius) {
        let circumference = 2 * Math.PI * radius;
        return (circumference * value / total) + ' ' + circumference;
      },
      focusSearch(event) {
        if ((event.metaKey || event.ctrlKey) && event.key && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          this.$refs.search && this.$refs.search.focus();
        }
      }
    },
    created() {
      // /contracts opens the list grouped by contract (the old contracts list)
      let saved = {};
      try {
        saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
      } catch (e) {
        // Ignore unreadable or blocked storage
      }
      if (saved.lens) {
        this.lens = saved.lens;
      }
      if (this.$route.query.group === 'contract') {
        this.group = 'contract';
      } else if (saved.group) {
        this.group = saved.group;
      }
      this.loadOverview();
    },
    mounted() {
      window.addEventListener('keydown', this.focusSearch);
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(entries => {
          this.tableWidth = entries[0].contentRect.width;
        });
        this.resizeObserver.observe(this.$refs.table);
      }
    },
    beforeDestroy() {
      window.removeEventListener('keydown', this.focusSearch);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }
  }
</script>
